import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail } from 'lucide-react';
import { auth as authApi } from '../utils/api';
import AvvisoIscrizioniChiuse from '../components/shared/AvvisoIscrizioniChiuse';
import { CODICE_ISCRIZIONI_CHIUSE, MSG_ISCRIZIONI_CHIUSE } from './iscrizioniChiuse';

// Cantiere Registrazione, Fetta 5c (DEC-067). Registrazione via email/password.
// Primo passo: email + password → il backend invia un link di verifica. La
// password NON viaggia nel link (è hashata lato server): qui la si sceglie e
// basta. Dopo la conferma dell'email si arriva al form di adesione (stesso del
// flusso SSO), con verifica telefono via SMS e controllo anti-abuso.
const MSG_ERRORE = {
  email_non_valida: 'L\'indirizzo email non è valido.',
  password_debole: 'La password è troppo debole. Usa almeno 12 caratteri, con una maiuscola, un numero e un simbolo.',
  registrazione_chiusa: MSG_ISCRIZIONI_CHIUSE,
};

export default function RegistratiEmail() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [inviato, setInviato] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chiuse, setChiuse] = useState(false);  // DBT-228
  // CAP-024 (DEC-176): si chiede al server PRIMA di mostrare il modulo, così con
  // le iscrizioni chiuse non si chiede mai una password. Se la domanda fallisce
  // si mostra il modulo: l'invio riceverebbe comunque il 403 e l'avviso.
  const [controllo, setControllo] = useState(true);

  useEffect(() => {
    let attivo = true;
    authApi.statoIscrizioni()
      .then(({ data }) => { if (attivo && data && data.aperte === false) setChiuse(true); })
      .catch(() => {})
      .finally(() => { if (attivo) setControllo(false); });
    return () => { attivo = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.registerEmailStart({ email: email.trim().toLowerCase(), password });
      setInviato(true);
    } catch (err) {
      const codice = err.response?.data?.codice;
      if (codice === CODICE_ISCRIZIONI_CHIUSE) {
        setChiuse(true);
        return;
      }
      setError(
        MSG_ERRORE[codice] ||
        err.response?.data?.error ||
        'Non siamo riusciti ad avviare la registrazione. Riprova tra qualche istante.',
      );
    } finally {
      setLoading(false);
    }
  };

  if (controllo) {
    return (
      <Guscio titolo="Registrati">
        <div data-testid="controllo-iscrizioni" aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ height: 40, borderRadius: 'var(--radius-base)', background: 'var(--chip)' }} />
          ))}
        </div>
      </Guscio>
    );
  }

  if (chiuse) {
    return (
      <Guscio titolo="Iscrizioni in arrivo">
        <AvvisoIscrizioniChiuse />
        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ds-muted)', marginTop: 16, marginBottom: 0 }}>
          Hai già un account?{' '}
          <button onClick={() => navigate('/auth/login')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)' }}>
            Accedi
          </button>
        </p>
      </Guscio>
    );
  }

  if (inviato) {
    return (
      <Guscio titolo="Controlla la tua email">
        <div style={{ textAlign: 'center' }}>
          <Mail size={44} strokeWidth={1.5} color="var(--brand)" style={{ margin: '0 auto 16px', display: 'block' }} />
          <p style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6, marginBottom: 20 }}>
            Se l'indirizzo è disponibile, ti abbiamo inviato un'email per
            confermarlo. Aprila e clicca il link per proseguire. Controlla anche
            la posta indesiderata. Il link scade tra 1 ora.
          </p>
          <button onClick={() => navigate('/auth/login')} className="gradient-brand" style={btnPrimario}>
            Torna all'accesso
          </button>
        </div>
      </Guscio>
    );
  }

  return (
    <Guscio titolo="Registrati con email">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 12, color: 'var(--ds-muted)', margin: 0 }}>
          Scegli email e password: ti manderemo un link per confermare
          l'indirizzo, poi completerai i dati della tua attività.
        </p>
        <div>
          <label style={labelStyle}>Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@azienda.it" required style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'} value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required style={inputStyle}
            />
            <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {showPw ? <Eye size={16} strokeWidth={2} color="var(--ds-muted)" /> : <EyeOff size={16} strokeWidth={2} color="var(--ds-muted)" />}
            </button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--ds-muted)', lineHeight: 1.5, margin: '6px 0 0' }}>
            Almeno 12 caratteri, con una maiuscola, un numero e un simbolo.
          </p>
        </div>
        {error && <div style={erroreStyle}>{error}</div>}
        <button type="submit" disabled={loading} className="gradient-brand" style={{ ...btnPrimario, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Invio in corso…' : 'Invia il link di conferma'}
        </button>
      </form>

      <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ds-muted)', marginTop: 16 }}>
        Hai già un account?{' '}
        <button onClick={() => navigate('/auth/login')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)' }}>
          Accedi
        </button>
      </p>
    </Guscio>
  );
}

const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--ds-text)', display: 'block', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
  background: 'var(--chip)', fontSize: 12, color: 'var(--ds-text)', boxSizing: 'border-box',
};
const btnPrimario = {
  width: '100%', padding: '11px 0', borderRadius: 'var(--radius-base)', border: 'none',
  cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'white', boxShadow: '0 2px 10px rgba(35,38,47,0.06)',
};
const erroreStyle = {
  fontSize: 11, color: 'var(--ds-danger)', background: 'color-mix(in srgb, var(--ds-danger) 8%, transparent)',
  borderRadius: 'var(--radius-base)', padding: '8px 12px',
};

// Guscio grafico condiviso (stessa veste del Login/Registrati).
function Guscio({ titolo, children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--chip)', padding: '32px 0' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
          <div className="gradient-brand" style={{ width: 52, height: 52, borderRadius: 'var(--radius-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
            <span style={{ color: 'white', fontWeight: 500, fontSize: 22, fontStyle: 'italic' }}>m</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>mAIstro</div>
        </div>
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: 28, boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginTop: 0, marginBottom: 16, fontFamily: 'var(--font-display)' }}>{titolo}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}
